import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilteredAndSortedDevices } from '../redux/devicesListSlice';
import DeviceInformation from '../device_cards/ai_card/DeviceInformation';
import DeviceTableRow from './DeviceTableRow';
import { setExpandedDevice } from '../redux/devicesListSlice';

const ITEMS_PER_PAGE = 25;

const DevicesTableBody = () => {
  const dispatch = useDispatch();
  const expandedDevice = useSelector((state) => state.devicesList.expandedDevice);
  const filteredAndSortedDevices = useSelector(selectFilteredAndSortedDevices);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(filteredAndSortedDevices.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedDevices = filteredAndSortedDevices.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const getUpcomingMaintenances = useCallback((device) => {
    if (!device.maintenances || device.maintenances.length === 0) return '-';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingMaintenance = device.maintenances
      .filter(m => {
        const maintenanceDate = new Date(m.date);
        maintenanceDate.setHours(0, 0, 0, 0);
        return maintenanceDate >= today;
      })
      .map(m => m.task)
      .join(', ');

    return upcomingMaintenance || '-';
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 1: return 'bg-status-success';
      case 2: return 'bg-status-warning';
      case 3: return 'bg-status-error';
      default: return 'bg-status-default';
    }
  }, []);

  const getStatusText = useCallback((status) => {
    switch (status) {
      case 1: return 'Good';
      case 2: return 'Urgent';
      case 3: return 'Attention';
      default: return 'NA';
    }
  }, []);

  const handleExpandDevice = useCallback((deviceId) => {
    dispatch(setExpandedDevice(expandedDevice === deviceId ? null : deviceId));
  }, [dispatch, expandedDevice]);

  return (
    <>
      <tbody className="bg-white relative divide-y divide-gray-200">
        {paginatedDevices.map((device) => (
          <DeviceTableRow
            key={device.sensor_id}
            device={device}
            isExpanded={expandedDevice === device.sensor_id}
            onExpandClick={handleExpandDevice}
            getUpcomingMaintenances={getUpcomingMaintenances}
            getStatusColor={getStatusColor}
            getStatusText={getStatusText}
          />
        ))}
      </tbody>
      {totalPages > 1 && (
        <tfoot>
          <tr>
            <td colSpan="100%">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
                <span className="text-sm text-gray-600">
                  Showing {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, filteredAndSortedDevices.length)} of {filteredAndSortedDevices.length} devices
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    ««
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    ‹
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 text-sm rounded border ${
                          currentPage === page
                            ? 'bg-[#1a2b4a] text-white border-[#1a2b4a]'
                            : 'border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    ›
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    »»
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      )}
    </>
  );
};

export default React.memo(DevicesTableBody);
